import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type OnboardingState, api } from "../lib/api";
import { Notice, SandboxPanel } from "../components/Shell";

const MESSAGES: Record<string, { tone: "good" | "bad" | "warn" | "info"; text: string }> = {
  HOLD: { tone: "warn", text: "Your KYC is on hold with the KRA. Update the details below with your broker or KRA, then check again." },
  BLOCKED: { tone: "bad", text: "Your KYC record is deactivated. Please contact the KRA or your broker to reactivate it, then check again." },
  INVALID_PAN: { tone: "bad", text: "That PAN does not look valid. Check it and try again." },
  RETRY: { tone: "warn", text: "The KRA could not confirm your status just now. Please try again in a few minutes." },
  MANUAL_REVIEW: { tone: "warn", text: "Your KYC status needs a manual check. Our team will contact you within one working day." },
  FRESH_KYC: { tone: "info", text: "We could not find a usable KYC record for this PAN. Complete a quick eKYC with DigiLocker and a selfie." },
};

export function KycStep({ state, session, onUpdate }: { state: OnboardingState; session: string | null; onUpdate: (s: OnboardingState) => void }) {
  const [pan, setPan] = useState(state.kyc?.pan ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ekyc, setEkyc] = useState<{ sessionId: string; redirectUrl: string } | null>(null);

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  // Returning from the live eKYC provider: finish the session named in the URL.
  useEffect(() => {
    if (session && !session.startsWith("sbx_")) {
      void run(async () => onUpdate(await api<OnboardingState>("/v1/onboarding/kyc/ekyc/complete", { body: { sessionId: session } })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const check = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => onUpdate(await api<OnboardingState>("/v1/onboarding/kyc", { body: { pan } })));
  };

  const startEkyc = () =>
    run(async () => {
      const s = await api<{ sessionId: string; redirectUrl: string }>("/v1/onboarding/kyc/ekyc/start", { method: "POST" });
      if (s.redirectUrl.includes("sandbox=")) setEkyc(s);
      else window.location.href = s.redirectUrl;
    });

  const finishSandbox = (outcome: "approve" | "fail") =>
    run(async () => {
      await api(`/v1/sandbox/sessions/${ekyc!.sessionId}/${outcome}`, { method: "POST" });
      try {
        onUpdate(await api<OnboardingState>("/v1/onboarding/kyc/ekyc/complete", { body: { sessionId: ekyc!.sessionId } }));
      } finally {
        setEkyc(null);
      }
    });

  const k = state.kyc;
  const msg = k && !k.canContinue ? MESSAGES[k.nextStep] : null;

  return (
    <div>
      <h1>KYC check</h1>
      <p className="muted">We check your PAN with the KYC Registration Agency (CVL KRA). If you have traded through a broker, your KYC is usually already done.</p>
      {error && <Notice tone="bad">{error}</Notice>}
      {msg && (
        <Notice tone={msg.tone}>
          {msg.text}
          {k!.missingFields.length > 0 && <ul>{k!.missingFields.map((f) => <li key={f}>KRA field code {f}</li>)}</ul>}
        </Notice>
      )}
      <form onSubmit={check}>
        <label className="field">
          <span>PAN</span>
          <input
            type="text"
            className="num"
            autoCapitalize="characters"
            maxLength={10}
            placeholder="ABCDE1234F"
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase())}
            required
          />
          <small>Your date of birth from the previous step is matched with the KRA record.</small>
        </label>
        <button className={k?.nextStep === "FRESH_KYC" ? "btn btn-quiet" : "btn"} disabled={busy || pan.length !== 10}>
          {busy && !ekyc ? "Checking…" : k ? "Check again" : "Check my KYC"}
        </button>
      </form>
      {k?.nextStep === "FRESH_KYC" && !ekyc && (
        <div style={{ marginTop: 16 }}>
          <button className="btn" onClick={() => void startEkyc()} disabled={busy}>
            Start eKYC
          </button>
        </div>
      )}
      {ekyc && (
        <SandboxPanel title="DigiLocker eKYC">
          <p>Decide how this test eKYC ends.</p>
          <div className="row">
            <button className="btn" onClick={() => void finishSandbox("approve")} disabled={busy}>
              Verify me
            </button>
            <button className="btn btn-quiet" onClick={() => void finishSandbox("fail")} disabled={busy}>
              Fail it
            </button>
          </div>
        </SandboxPanel>
      )}
    </div>
  );
}
